import { useQuery } from '@tanstack/react-query';
import { PageHeader, Badge, Button } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { fetchContacts } from '@/data/sources';
import { useActiveDomain } from '@/hooks/useActiveDomain';

/* /sales/crm/contacts — thin CRM contacts list (brief v1 §4b). */
export function ContactsPage() {
  const { active } = useActiveDomain();
  // Keyed by projectId so a domain switch refetches. Contacts are NOT domain
  // rows (bc_contacts has no host field) — they stay workspace-wide and are
  // labelled "Across workspace" below, never mislabelled as a domain's.
  const { data: contacts, isPending, error, refetch } = useQuery({
    queryKey: ['crm', 'contacts', active?.projectId ?? 'all'],
    queryFn: () => fetchContacts(active?.projectId),
  });

  return (
    <div>
      <PageHeader
        title="Contacts"
        subtitle="People in your CRM — deduped, with source attribution from Outreach."
        actions={<Button variant="primary">＋ Add contact</Button>}
      />
      {active && (
        <div className="mb-4 rounded-md border border-border-subtle bg-inset/40 px-3 py-2 text-xs text-text-tertiary">
          Showing contacts across your whole workspace — a contact isn't tied to a single
          domain, so this list isn't filtered by the domain you're viewing ({active.hostname}).
        </div>
      )}
      {isPending ? (
        <LoadingState rows={5} />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : contacts.length === 0 ? (
        <EmptyState
          title="No contacts yet"
          teach="Contacts land here when you promote a lead from Outreach — each arrives deduped, with its campaign, lead reference and score snapshot attached — or add one manually."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr className="border-b border-border-subtle bg-inset/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
                  <th className="px-3 py-2 font-semibold">Name</th>
                  <th className="px-3 py-2 font-semibold">Company</th>
                  <th className="px-3 py-2 font-semibold">Title</th>
                  <th className="px-3 py-2 font-semibold">Stage</th>
                  <th className="px-3 py-2 font-semibold">Source</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} className="border-b border-border-subtle last:border-0 hover:bg-hover">
                    <td className="px-3 py-2.5">
                      <div className="text-sm font-medium text-text-primary">{c.name}</div>
                      <div className="text-xs text-text-tertiary">{c.email}</div>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-text-secondary">{c.company || '—'}</td>
                    <td className="px-3 py-2.5 text-sm text-text-secondary">{c.title}</td>
                    <td className="px-3 py-2.5"><Badge tone="compliance">{c.stage}</Badge></td>
                    <td className="px-3 py-2.5 text-xs text-text-tertiary">
                      {c.source_attribution ?? <span className="italic">manual</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
